import { useState, useEffect } from 'react';
import { adminAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { toast } from 'sonner';
import { Save } from 'lucide-react';

const contentSections = [
  {
    id: 'header', title: 'Header',
    fields: [
      { key: 'header_titel', label: 'Header Titel', type: 'input' },
      { key: 'header_subtitel', label: 'Header Subtitel', type: 'input' },
      { key: 'kooperation_hinweis', label: 'Kooperationshinweis', type: 'input' },
      { key: 'weiterleitung_hinweis', label: 'Weiterleitungshinweis', type: 'input' },
    ]
  },
  {
    id: 'hero', title: 'Hero',
    note: 'Diese Felder werden direkt im Hero-Bereich der Landingpage angezeigt. Aenderungen sind sofort nach Speichern und Seitenreload sichtbar.',
    fields: [
      { key: 'hero_ueberschrift', label: 'Ueberschrift (H1)', type: 'input' },
      { key: 'hero_unterzeile', label: 'Unterzeile', type: 'input' },
      { key: 'hero_button', label: 'Button Text', type: 'input' },
      { key: 'hero_hinweistext', label: 'Hinweistext', type: 'input' },
      { key: 'hero_teaserpreis', label: 'Teaserpreis', type: 'input' },
      { key: 'hero_preishinweis', label: 'Preishinweis', type: 'input' },
      { key: 'hero_uebernahmehinweis', label: 'Uebernahmehinweis', type: 'input' },
    ]
  },
  {
    id: 'mobilitaet', title: 'Mobilität',
    fields: [
      { key: 'mobilitaet_titel', label: 'Titel', type: 'input' },
      { key: 'mobilitaet_text', label: 'Text', type: 'textarea' },
    ]
  },
  {
    id: 'uebernahmeorte', title: 'Übernahmeorte',
    fields: [
      { key: 'uebernahmeorte_titel', label: 'Titel', type: 'input' },
      { key: 'uebernahmeorte_einleitung', label: 'Einleitungstext', type: 'textarea' },
      { key: 'standort1', label: 'Standort 1 Name', type: 'input' },
      { key: 'standort1_adresse', label: 'Standort 1 Adresse', type: 'input' },
      { key: 'standort2', label: 'Standort 2 Name', type: 'input' },
      { key: 'standort2_adresse', label: 'Standort 2 Adresse', type: 'input' },
      { key: 'standort3', label: 'Standort 3 Name', type: 'input' },
      { key: 'standort3_adresse', label: 'Standort 3 Adresse', type: 'input' },
      { key: 'standort4', label: 'Standort 4 (optional)', type: 'input' },
    ]
  },
  {
    id: 'vorteile', title: 'Vorteile',
    fields: [
      { key: 'vorteile_titel', label: 'Titel', type: 'input' },
      { key: 'vorteil1_titel', label: 'Vorteil 1 Titel', type: 'input' },
      { key: 'vorteil1_text', label: 'Vorteil 1 Text', type: 'textarea' },
      { key: 'vorteil2_titel', label: 'Vorteil 2 Titel', type: 'input' },
      { key: 'vorteil2_text', label: 'Vorteil 2 Text', type: 'textarea' },
      { key: 'vorteil3_titel', label: 'Vorteil 3 Titel', type: 'input' },
      { key: 'vorteil3_text', label: 'Vorteil 3 Text', type: 'textarea' },
      { key: 'vorteil4_titel', label: 'Vorteil 4 Titel', type: 'input' },
      { key: 'vorteil4_text', label: 'Vorteil 4 Text', type: 'textarea' },
    ]
  },
  {
    id: 'ideal', title: 'Ideal-Sektion',
    fields: [
      { key: 'ideal_titel', label: 'Titel', type: 'input' },
      { key: 'ideal_text', label: 'Text', type: 'textarea' },
    ]
  },
  {
    id: 'ablauf', title: 'Ablauf',
    fields: [
      { key: 'ablauf_titel', label: 'Titel', type: 'input' },
      { key: 'schritt1_titel', label: 'Schritt 1 Titel', type: 'input' },
      { key: 'schritt1_text', label: 'Schritt 1 Text', type: 'textarea' },
      { key: 'schritt2_titel', label: 'Schritt 2 Titel', type: 'input' },
      { key: 'schritt2_text', label: 'Schritt 2 Text', type: 'textarea' },
      { key: 'schritt3_titel', label: 'Schritt 3 Titel', type: 'input' },
      { key: 'schritt3_text', label: 'Schritt 3 Text', type: 'textarea' },
    ]
  },
  {
    id: 'faq', title: 'FAQ',
    fields: [
      { key: 'faq_titel', label: 'FAQ Titel', type: 'input' },
      { key: 'faq1_frage', label: 'Frage 1', type: 'input' },
      { key: 'faq1_antwort', label: 'Antwort 1', type: 'textarea' },
      { key: 'faq2_frage', label: 'Frage 2', type: 'input' },
      { key: 'faq2_antwort', label: 'Antwort 2', type: 'textarea' },
      { key: 'faq3_frage', label: 'Frage 3', type: 'input' },
      { key: 'faq3_antwort', label: 'Antwort 3', type: 'textarea' },
      { key: 'faq4_frage', label: 'Frage 4', type: 'input' },
      { key: 'faq4_antwort', label: 'Antwort 4', type: 'textarea' },
    ]
  },
  {
    id: 'cta', title: 'CTA',
    fields: [
      { key: 'cta_titel', label: 'CTA Titel', type: 'input' },
      { key: 'cta_text', label: 'CTA Text', type: 'textarea' },
      { key: 'cta_button', label: 'CTA Button', type: 'input' },
    ]
  },
  {
    id: 'marketing', title: 'Marketing-Hinweise',
    fields: [
      { key: 'trust_hinweis', label: 'Trust-Banner Text (blauer Banner unter Hero)', type: 'input' },
      { key: 'verfuegbarkeits_hinweis', label: 'Verfuegbarkeits-Hinweis (im CTA-Bereich)', type: 'input' },
      { key: 'schnell_standorte_titel', label: 'Schnell-Standorte Titel', type: 'input' },
    ]
  },
  {
    id: 'footer', title: 'Footer',
    fields: [
      { key: 'footer_text', label: 'Footer Text', type: 'input' },
      { key: 'footer_affiliate_hinweis', label: 'Affiliate Hinweis', type: 'textarea' },
      { key: 'footer_markenhinweis', label: 'Markenhinweis', type: 'textarea' },
    ]
  },
];

export default function ContentEditor() {
  const [content, setContent] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    adminAPI.getContent().then(res => setContent(res.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleChange = (key, value) => {
    setContent(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminAPI.updateContent(content);
      toast.success('Inhalte gespeichert');
    } catch {
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0E518D]" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="content-editor">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Inhalte bearbeiten</h1>
          <p className="text-sm text-slate-500 font-body mt-1">Überschrift, Untertitel, Hero, Vorteile und FAQ verwalten</p>
        </div>
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-[#0E518D] hover:bg-[#0B4171] text-white rounded-sm font-body"
          data-testid="content-save-button"
        >
          <Save className="w-4 h-4 mr-2" /> {saving ? 'Speichern...' : 'Speichern'}
        </Button>
      </div>

      <Accordion type="multiple" defaultValue={['header', 'hero']} className="space-y-3">
        {contentSections.map(section => (
          <AccordionItem key={section.id} value={section.id} className="bg-white border border-slate-200 rounded-sm">
            <AccordionTrigger className="px-5 py-4 font-body font-semibold text-slate-800 hover:no-underline">
              {section.title}
            </AccordionTrigger>
            <AccordionContent className="px-5 pb-5 space-y-4">
              {section.note && (
                <div className="bg-blue-50 border border-blue-200 rounded-sm px-4 py-2.5 text-xs text-blue-800 font-body" data-testid={`section-note-${section.id}`}>
                  {section.note}
                </div>
              )}
              {section.fields.map(field => (
                <div key={field.key}>
                  <Label className="font-body text-sm text-slate-600">{field.label}</Label>
                  {field.type === 'textarea' ? (
                    <Textarea
                      value={content[field.key] || ''}
                      onChange={(e) => handleChange(field.key, e.target.value)}
                      className="mt-1 rounded-sm font-body"
                      rows={3}
                      data-testid={`content-field-${field.key}`}
                    />
                  ) : (
                    <Input
                      value={content[field.key] || ''}
                      onChange={(e) => handleChange(field.key, e.target.value)}
                      className="mt-1 rounded-sm font-body"
                      data-testid={`content-field-${field.key}`}
                    />
                  )}
                </div>
              ))}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
}
